import React from 'react';
import { InlineErrorFallback } from './ErrorFallback';

interface FeatureErrorBoundaryProps {
  children: React.ReactNode;
  /** Height for the fallback UI (matches component dimensions) */
  fallbackHeight?: string | number;
  /** Custom message for the fallback UI */
  fallbackMessage?: string;
  /** Callback when an error is caught */
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  /** Maximum retry attempts before showing "unavailable" (default: 3) */
  maxRetries?: number;
  /** Initial retry delay in ms, doubles on each retry (default: 1000) */
  retryDelay?: number;
  /** Keys that trigger a reset when they change */
  resetKeys?: unknown[];
  /** Feature name for logging */
  featureName?: string;
}

interface FeatureErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  retryCount: number;
}

/**
 * Feature-level error boundary for inline protection of high-risk components.
 * 
 * Provides:
 * - Inline error display that matches component dimensions
 * - Automatic retry with exponential backoff
 * - Reset when `resetKeys` change
 * - Configurable max retries
 * 
 * Use for: StudySession, FlashCard, AI-powered components, etc.
 */
export class FeatureErrorBoundary extends React.Component<
  FeatureErrorBoundaryProps,
  FeatureErrorBoundaryState
> {
  private retryTimeoutId: ReturnType<typeof setTimeout> | null = null;

  static defaultProps = {
    maxRetries: 3,
    retryDelay: 1000,
    fallbackMessage: 'This feature failed to load',
  };

  constructor(props: FeatureErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, retryCount: 0 };
  }

  static getDerivedStateFromError(error: Error): Partial<FeatureErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    const featureName = this.props.featureName || 'Unknown Feature';
    console.error(`[FeatureErrorBoundary:${featureName}] Caught error:`, error);
    console.error(`[FeatureErrorBoundary:${featureName}] Component stack:`, errorInfo.componentStack);
    
    this.props.onError?.(error, errorInfo);
  }

  componentDidUpdate(prevProps: FeatureErrorBoundaryProps): void {
    // Reset when resetKeys change
    if (this.state.hasError && this.props.resetKeys) {
      const prevKeys = prevProps.resetKeys || [];
      const currentKeys = this.props.resetKeys;
      
      const keysChanged = currentKeys.some((key, index) => key !== prevKeys[index]);
      
      if (keysChanged) {
        this.handleReset();
      }
    }
  }

  componentWillUnmount(): void {
    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId);
    }
  }

  handleReset = (): void => {
    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId);
    }

    const maxRetries = this.props.maxRetries ?? 3;
    
    if (this.state.retryCount < maxRetries) {
      // Apply exponential backoff delay before reset
      const delay = (this.props.retryDelay ?? 1000) * Math.pow(2, this.state.retryCount);
      
      this.retryTimeoutId = setTimeout(() => {
        this.setState(prev => ({
          hasError: false,
          error: null,
          retryCount: prev.retryCount + 1,
        }));
      }, Math.min(delay, 100)); // Cap at 100ms for immediate feel, backoff applies to repeated failures
    }
  };

  render(): React.ReactNode {
    const maxRetries = this.props.maxRetries ?? 3;
    const retriesRemaining = maxRetries - this.state.retryCount;

    if (this.state.hasError) {
      return (
        <InlineErrorFallback
          error={this.state.error}
          onReset={this.handleReset}
          height={this.props.fallbackHeight}
          message={this.props.fallbackMessage}
          retriesRemaining={retriesRemaining}
        />
      );
    }

    return this.props.children;
  }
}
